import { useEffect } from 'react';
import { FooterSection, Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { useMediaQuery } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  BoldAction,
  Button,
  ColorPickerAction,
  FontPickerAction,
  ItalicAction,
  SizePickerAction,
  Text,
  TextField,
  useForm,
  useFormField,
} from '@frontend/design-system';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { FooterLinkType, SetComposerSectionProps } from '../../content-composer/types';
import { ExpansionButton } from '../atoms/expansion-button';
import { PopoverButtonGroup } from '../atoms/popover-button-group';
import { ToolbarButtonGroup } from '../atoms/toolbar-button-group';
import { useLinks } from '../hooks/use-links';
import { FooterMode } from './types';

type NonCustomLinkProps = {
  links?: Link[];
  selectedLink?: Link;
  setMode?: (mode: FooterMode) => void;
  setProps: SetComposerSectionProps<FooterSection>;
};

const NonCustomLink = ({ links, selectedLink, setMode, setProps }: NonCustomLinkProps) => {
  const { t } = useTranslation('content-composer');
  const fieldProps = useFormField(
    {
      type: 'text',
      value: selectedLink?.url,
    },
    [selectedLink]
  );

  const handleUpdate = () => {
    if (!fieldProps.value) return;
    const newLink = { linkType: selectedLink?.linkType ?? 'custom', url: fieldProps.value, text: fieldProps.value };
    // the link already exists, so just update the link
    if (selectedLink?.url && (!links || links?.find((link) => link.linkType === selectedLink.linkType))) {
      setProps((props) => {
        const index = props.links?.findIndex((link) => link.linkType === selectedLink?.linkType) ?? -1;
        if (index === -1) return;
        if (props.links === undefined) {
          props.links = [];
        }
        props.links[index] = newLink;
      });
    } else {
      setProps((props) => (props.links = props.links ? [...props.links, newLink] : [newLink]));
    }
    setMode?.('options');
  };

  return (
    <SectionBase.Toolbar.Tool css={{ padding: 0 }}>
      <Button
        iconName='back-small'
        variant='secondary'
        size='small'
        onClick={() => setMode?.('options')}
        css={{
          borderBottomRightRadius: 0,
          borderRight: `solid 1px ${theme.colors.neutral20}`,
          borderTopRightRadius: 0,
          marginRight: theme.spacing(1),
          svg: { color: theme.colors.neutral70 },
          ':hover, :focus': { color: theme.colors.neutral10 },
        }}
      />
      <TextField
        {...fieldProps}
        name=''
        label=''
        placeholder={
          selectedLink?.linkType === 'phone'
            ? t('Phone')
            : selectedLink?.linkType === 'email'
            ? t('Email')
            : t('Link URL')
        }
        css={{
          width: 400,
          border: '0 !important',
          boxShadow: 'none !important',
          height: theme.spacing(4),
        }}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleUpdate();
          }
        }}
      />
      <Button
        disabled={!fieldProps.value}
        iconName='check'
        onClick={handleUpdate}
        size='small'
        variant='primary'
        css={{
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0,
          marginLeft: theme.spacing(1),
        }}
      />
    </SectionBase.Toolbar.Tool>
  );
};

type CustomLinkProps = {
  links?: Link[];
  selectedLink?: Link;
  setMode?: (mode: FooterMode) => void;
  setProps: SetComposerSectionProps<FooterSection>;
};

const CustomLink = ({ selectedLink, setMode, setProps }: CustomLinkProps) => {
  const { t } = useTranslation('content-composer');
  const { getFieldProps } = useForm({
    fields: {
      linkText: { type: 'text', value: selectedLink?.text },
      link: { type: 'text', value: selectedLink?.url },
    },
  });

  const linkFieldProps = getFieldProps('link');
  const linkTextFieldProps = getFieldProps('linkText');

  useEffect(() => {
    linkFieldProps.onChange({ ...linkFieldProps, value: selectedLink?.url });
    linkTextFieldProps.onChange({ ...linkTextFieldProps, value: selectedLink?.text });
  }, [selectedLink]);

  const handleUpdate = () => {
    if (linkFieldProps.value || linkTextFieldProps.value) {
      const newLink = { linkType: 'custom', url: linkFieldProps.value, text: linkTextFieldProps.value };
      if (selectedLink?.url) {
        setProps((props) => {
          const index = props.links?.findIndex((link) => link.url === selectedLink?.url) ?? -1;
          if (index === -1) return;
          if (props.links === undefined) {
            props.links = [];
          }
          props.links[index] = newLink;
        });
      } else {
        setProps((props) => (props.links = props.links ? [...props.links, newLink] : [newLink]));
      }
      setMode?.('options');
    }
  };

  const isLinkComplete = !!linkFieldProps.value && !!linkTextFieldProps.value;
  return (
    <SectionBase.Toolbar.Tool css={{ padding: 0 }}>
      <Button
        iconName='back-small'
        variant='secondary'
        size='small'
        onClick={() => setMode?.('options')}
        css={{
          borderBottomRightRadius: 0,
          borderRight: `solid 1px ${theme.colors.neutral20}`,
          borderTopRightRadius: 0,
          marginRight: theme.spacing(1),
          svg: { color: theme.colors.neutral70 },
          ':hover, :focus': { color: theme.colors.neutral10 },
        }}
      />
      <TextField
        {...linkTextFieldProps}
        name='linkText'
        label=''
        autoFocus
        placeholder={t('Enter Link Text')}
        css={{
          width: 200,
          border: '0 !important',
          borderRight: `solid 1px ${theme.colors.neutral20} !important`,
          borderRadius: 0,
          boxShadow: 'none !important',
          height: theme.spacing(4),
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleUpdate();
          }
        }}
      />
      <TextField
        {...linkFieldProps}
        name='link'
        label=''
        placeholder={t('Enter Link URL')}
        css={{
          width: 200,
          border: '0 !important',
          boxShadow: 'none !important',
          height: theme.spacing(4),
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleUpdate();
          }
        }}
      />
      <Button
        iconName='check'
        variant='primary'
        disabled={!isLinkComplete}
        size='small'
        onClick={handleUpdate}
        css={{
          borderBottomLeftRadius: 0,
          borderLeft: `solid 1px ${isLinkComplete ? theme.colors.primary50 : theme.colors.status.disabled}`,
          borderTopLeftRadius: 0,
          marginLeft: theme.spacing(1),
          svg: { color: isLinkComplete ? theme.colors.white : theme.colors.status.disabled },
          ':hover, :focus': { color: isLinkComplete ? theme.colors.white : theme.colors.status.disabled },
        }}
      />
    </SectionBase.Toolbar.Tool>
  );
};

type FooterToolbarProps = {
  mode?: FooterMode;
  selectedLink?: Link;
  setMode?: (mode: FooterMode) => void;
  setSelectedLink?: (link?: Link) => void;
};

export const FooterToolbar = ({ mode, setMode, selectedLink, setSelectedLink }: FooterToolbarProps) => {
  const { t } = useTranslation('content-composer');
  const { email, phone } = useLinks();
  const {
    setProps,
    props: { bold, fontType, italic, links, textColor, textSize },
  } = useComposerSection<FooterSection>();

  const EXTRA_LARGE_BREAKPOINT = 1450;
  const LARGE_BREAKPOINT = 1200;
  const MEDIUM_BREAKPOINT = 850;
  const SMALL_BREAKPOINT = 600;

  const fontPicker = (
    <FontPickerAction
      css={{ fontWeight: theme.font.weight.bold }}
      value={fontType}
      onChange={(font) => setProps((props) => (props.fontType = font))}
    />
  );

  const sizePicker = (
    <SizePickerAction
      size={textSize}
      onChange={(size) => setProps((props) => (props.textSize = size))}
      css={{
        button: {
          width: 65,
        },
        span: {
          fontWeight: theme.font.weight.bold,
        },
      }}
    />
  );

  const colorPicker = (
    <ColorPickerAction
      color={textColor}
      onChange={(color) => setProps((props) => (props.textColor = color))}
      css={{ fontWeight: theme.font.weight.bold }}
    />
  );

  const boldPicker = <BoldAction isActive={bold} onClick={() => setProps((props) => (props.bold = !bold))} />;

  const italicPicker = <ItalicAction isActive={italic} onClick={() => setProps((props) => (props.italic = !italic))} />;

  const addLink = (showText: boolean) => (
    <SectionBase.Toolbar.Button onClick={() => setMode?.('options')}>
      <Icon name='plus-small' />
      {showText && t('Add Link')}
    </SectionBase.Toolbar.Button>
  );

  const initialState = (
    <div
      css={{
        display: 'flex',
        columnGap: theme.spacing(1),
        '& button': {
          boxShadow: theme.shadows.light,
          color: theme.colors.neutral70,
          fontSize: theme.font.size.medium,
          fontWeight: theme.font.weight.bold,
          height: 'initial',
          minHeight: theme.spacing(4),
          minWidth: theme.spacing(4),
          transition: 'all 250ms ease',
          ':hover, :focus': {
            background: theme.colors.neutral5,
            boxShadow: theme.shadows.floating,
            color: theme.colors.black,
          },
        },
      }}
    >
      {fontPicker}
      <ToolbarButtonGroup breakpoint={SMALL_BREAKPOINT}>{sizePicker}</ToolbarButtonGroup>
      <ToolbarButtonGroup breakpoint={MEDIUM_BREAKPOINT}>
        {colorPicker}
        {boldPicker}
      </ToolbarButtonGroup>
      <ToolbarButtonGroup breakpoint={LARGE_BREAKPOINT}>
        {italicPicker}
        {addLink(true)}
      </ToolbarButtonGroup>
      <ExpansionButton breakpoint={LARGE_BREAKPOINT}>
        <PopoverButtonGroup breakpoint={SMALL_BREAKPOINT}>{sizePicker}</PopoverButtonGroup>
        <PopoverButtonGroup breakpoint={MEDIUM_BREAKPOINT}>
          {colorPicker}
          {boldPicker}
        </PopoverButtonGroup>
        {italicPicker}
        {addLink(false)}
      </ExpansionButton>
    </div>
  );

  type OptionType = { linkType: FooterLinkType; label: string; iconName: IconName };

  const createLinkButton = (option: OptionType) => (
    <SectionBase.Toolbar.Button
      key={option.linkType}
      onClick={() => {
        if (option.linkType === 'custom') {
          setMode?.('custom');
        } else if (option.linkType === 'phone' || option.linkType === 'email') {
          setMode?.('options');
        } else {
          setMode?.('link');
        }

        if (option.linkType === 'phone') {
          const phoneOption = { linkType: option.linkType, url: phone, text: phone };
          setProps((props) => (props.links = props.links ? [...props.links, phoneOption] : [phoneOption]));
        } else if (option.linkType === 'email') {
          const emailOption = { linkType: option.linkType, url: email, text: email };
          setProps((props) => (props.links = props.links ? [...props.links, emailOption] : [emailOption]));
        } else {
          // TODO: get the other links from the BE and set them here as well
          setSelectedLink?.({ linkType: option.linkType, url: '', text: '' });
        }
      }}
    >
      <Icon name={option.iconName} css={{ marginRight: theme.spacing(0.5) }} />
      <Text as='span'>{option.label}</Text>
    </SectionBase.Toolbar.Button>
  );

  const includesLink = (option: OptionType): boolean | undefined => {
    if (option.linkType === 'custom') return true;
    return !links?.find((link) => link.linkType === option.linkType);
  };

  const options: OptionType[] = [
    { linkType: 'google', label: t('Google'), iconName: 'social-google-small' },
    { linkType: 'facebook', label: t('Facebook'), iconName: 'social-facebook-small' },
    { linkType: 'phone', label: t('Phone'), iconName: 'phone-small' },
    { linkType: 'email', label: t('Email'), iconName: 'email-small' },
    { linkType: 'custom', label: t('Custom'), iconName: 'link-small' },
  ];

  const filteredOptions = options.filter((option) => includesLink(option));
  const arr1 = filteredOptions.slice(0, 1);
  const arr2 = filteredOptions.filter((option) => !arr1.find((o) => o.linkType === option.linkType)).slice(0, 1);
  const arr3 = filteredOptions.filter(
    (option) => !arr1.find((o) => o.linkType === option.linkType) && !arr2.find((o) => o.linkType === option.linkType)
  );
  const isMedium = useMediaQuery(`(min-width: ${MEDIUM_BREAKPOINT}px) and (max-width: ${LARGE_BREAKPOINT}px)`);

  const optionsState = (
    <>
      <SectionBase.Toolbar.Button onClick={() => setMode?.('default')}>
        <Icon name='back-small' />
      </SectionBase.Toolbar.Button>
      {arr1.map(createLinkButton)}
      <ToolbarButtonGroup breakpoint={MEDIUM_BREAKPOINT}>{arr2.map(createLinkButton)}</ToolbarButtonGroup>
      <ToolbarButtonGroup breakpoint={EXTRA_LARGE_BREAKPOINT}>{arr3.map(createLinkButton)}</ToolbarButtonGroup>
      {((arr2.length > 0 && !isMedium) || arr3.length > 0) && (
        <ExpansionButton breakpoint={EXTRA_LARGE_BREAKPOINT} hideBorder>
          {arr2.length > 0 ? (
            <PopoverButtonGroup breakpoint={MEDIUM_BREAKPOINT}>{arr2.map(createLinkButton)}</PopoverButtonGroup>
          ) : null}
          {arr3.length > 0 ? arr3.map(createLinkButton) : null}
        </ExpansionButton>
      )}
    </>
  );

  const getContent = () => {
    if (mode === 'default') return initialState;
    else if (mode === 'custom') return <CustomLink setMode={setMode} setProps={setProps} selectedLink={selectedLink} />;
    else if (mode === 'link')
      return <NonCustomLink links={links} setMode={setMode} setProps={setProps} selectedLink={selectedLink} />;
    return optionsState;
  };

  return <SectionBase.Toolbar position='bottom-center'>{getContent()}</SectionBase.Toolbar>;
};
